import { type Dispatch, type FC, type SetStateAction, useMemo } from 'react'
import { GalleryModal } from '@/components/commons/gallery-modal'
import { GalleryModalHeader } from '@/components/commons/gallery-modal-header'
import { GalleryModalSidebar } from '@/components/commons/gallery-modal-sidebar'
import { GalleryModalThumbnails } from '@/components/commons/gallery-modal-thumbnails'
import { GalleryFlags } from '@/features/photoset/components/gallery-photoset-modal/gallery-flags/gallery-flags'
import { GalleryMetadata } from '@/features/photoset/components/gallery-photoset-modal/gallery-metadata/gallery-metadata'
import { useGalleryInitialQaShortcuts } from '@/features/photoset/hooks/use-gallery-initial-qa-shortcuts'
import { type Image, StepKey } from '@/features/photoset/types'
import { useTranslation } from '@/translations/i18n'

interface GalleryInitialQaModal {
	images: Image[]
	currentDetailImageIndex: number
	setCurrentDetailImageIndex: Dispatch<SetStateAction<number | null>>
	onClose: () => void
}

export const GalleryInitialQaModal: FC<GalleryInitialQaModal> = ({
	images,
	currentDetailImageIndex,
	setCurrentDetailImageIndex,
	onClose
}) => {
	const { t } = useTranslation()

	const currentImage = useMemo(
		() => images[currentDetailImageIndex],
		[currentDetailImageIndex, images]
	)

	useGalleryInitialQaShortcuts({
		images: [...images.values()],
		currentDetailImageIndex,
		setCurrentDetailImageIndex,
		onClose
	})

	return (
		<GalleryModal
			currentImage={currentImage}
			headerSlot={
				<GalleryModalHeader
					currentImage={currentImage}
					imagesCount={images.length}
					onClose={onClose}
				/>
			}
			onClose={onClose}
			sidebarSlot={
				<GalleryModalSidebar
					content={
						<>
							<GalleryMetadata
								imageMetadata={currentImage.metadata}
							/>
							<GalleryFlags
								image={currentImage}
								stepId={StepKey['initial-qa']}
							/>
						</>
					}
					title={t('photoset:curation.sidebar_title')}
				/>
			}
			thumbnails={currentImage.thumbnails}
			thumbnailsSlot={
				<GalleryModalThumbnails
					currentImageId={currentImage.id}
					images={[...images.values()]}
					onSelect={(_, index) => {
						setCurrentDetailImageIndex(index)
					}}
					thumbnails={'thumbnails'}
				/>
			}
		/>
	)
}
